import { useState, useEffect, useMemo, useRef, useDeferredValue } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import {
  Loader2,
  Send,
  Check,
  AlertCircle,
  Calculator,
  Info,
  CalendarIcon,
  Truck,
  ArrowRight,
  ArrowLeft,
  MapPin,
  Sparkles,
  ChevronDown,
  Settings,
  X,
  Gauge,
  Phone,
  MessageCircle,
} from "lucide-react";
import { notifyExternalInquiry, isCoolingInquiry } from "@/lib/notifyExternalInquiry";
import { isFreshOnlyCoolingVehicle } from "@/lib/vehicleCapabilities";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/lib/utils";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { differenceInDays, format, addDays } from "date-fns";
import { de } from "date-fns/locale";
import { trackFormSubmission, trackConversion, trackCTAClick, trackEvent, trackInquirySheetScroll, trackInquiryTimeToFirstFocus, trackInquiryValidationError } from "@/lib/analytics";
import { setInquirySheetFieldFocus, setInquirySheetScrollDepth } from "@/lib/inquirySheetTelemetry";
import { ScrollArea } from "@/components/ui/scroll-area";
import InquirySuccessDialog from "@/components/InquirySuccessDialog";
import { useInquiryDraft, clearInquiryDraft } from "@/hooks/useInquiryDraft";
import { PHONE_NUMBER, PHONE_DISPLAY, PHONE_WHATSAPP } from "@/lib/constants";
import { useIsMobile } from "@/hooks/use-mobile";

export type InquiryVehicleType = "kuehl" | "transporter";

interface InquiryFormProps {
  vehicleType: InquiryVehicleType;
  vehicleLabel: string;
  vehicleImage?: string;
  vehicleId?: string;
  variant?: "dialog" | "inline";
  source?: string;
  onClose?: () => void;
  onSubmitted?: () => void;
}

interface TechnicalSpec {
  label: string;
  value: string;
}

interface PricingItem {
  id: string;
  name: string;
  price_amount: number;
  price_unit: string;
  included_km: number | null;
  is_featured: boolean | null;
  rental_days: number | null;
  vehicle_id?: string | null;
}

const EXTRA_KM_RATE = 0.35;

const fieldValidation = {
  name: z.string().min(2, "min_2_chars"),
  phone: z.string().min(6, "min_6_chars"),
  email: z.string().email("invalid_email"),
};

// Soft-validation: optional fields only validate if filled
const validateIfFilled = (name: keyof typeof fieldValidation, value: string): string | null => {
  if (!value || !value.trim()) return null;
  try {
    fieldValidation[name].parse(value);
    return null;
  } catch (err) {
    if (err instanceof z.ZodError) return err.errors[0].message;
    return null;
  }
};

const RATE_LIMIT_KEY = "frostflotte_form_submissions";
const RATE_LIMIT_MAX = 3;
const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000;

function getRecentSubmissions(): number[] {
  try {
    const raw = localStorage.getItem(RATE_LIMIT_KEY);
    if (!raw) return [];
    return (JSON.parse(raw) as number[]).filter((t) => t > Date.now() - RATE_LIMIT_WINDOW_MS);
  } catch {
    return [];
  }
}

function recordSubmission() {
  const recent = getRecentSubmissions();
  recent.push(Date.now());
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recent));
}

function isClientRateLimited() {
  const recent = getRecentSubmissions();
  if (recent.length >= RATE_LIMIT_MAX) {
    const retryAt = Math.min(...recent) + RATE_LIMIT_WINDOW_MS;
    return { limited: true, retryInSeconds: Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)) };
  }
  return { limited: false, retryInSeconds: 0 };
}

const InquiryForm = ({
  vehicleType,
  vehicleLabel,
  vehicleImage,
  vehicleId,
  variant = "dialog",
  source,
  onClose,
  onSubmitted,
}: InquiryFormProps) => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === "en";
  const isKuehl = vehicleType === "kuehl";
  const isAdviceRequest = vehicleId === "__advice__";
  const isInline = variant === "inline";
  const isMobile = useIsMobile();
  const isMobileCompact = !!isMobile && variant === "dialog";
  const [step, setStep] = useState<1 | 2>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successContactMethod, setSuccessContactMethod] = useState<"phone" | "email">("phone");
  const [dateUnknown, setDateUnknown] = useState(false);
  const [contactPreference, setContactPreference] = useState<"phone" | "email">("phone");
  const [directOpen, setDirectOpen] = useState(false);
  const [pricingItems, setPricingItems] = useState<PricingItem[]>([]);
  const [technicalSpecs, setTechnicalSpecs] = useState<TechnicalSpec[]>([]);
  const [techDataOpen, setTechDataOpen] = useState(false);
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);

  const [formData, setFormData] = useState({
    pickupDate: null as Date | null,
    returnDate: null as Date | null,
    extraKm: "",
    deliveryEnabled: false,
    deliveryAddress: "",
    temperature: "",
    usage: "",
    name: "",
    phone: "",
    email: "",
    company: "",
    privacy: false,
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [fieldValid, setFieldValid] = useState<Record<string, boolean>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [pickupPopoverOpen, setPickupPopoverOpen] = useState(false);
  const [returnPopoverOpen, setReturnPopoverOpen] = useState(false);

  // Submit-Validierung: fehlendes Pflichtfeld wird rot markiert, beschriftet
  // und in den Blick gescrollt (statt nur Toast, der auf Mobil übersehen wird).
  const [submitErrors, setSubmitErrors] = useState<Record<string, string>>({});
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});
  const registerField = (name: string) => (el: HTMLElement | null) => {
    fieldRefs.current[name] = el;
  };
  const clearSubmitError = (name: string) =>
    setSubmitErrors((p) => {
      if (!p[name]) return p;
      const next = { ...p };
      delete next[name];
      return next;
    });
  const failSubmitValidation = (name: string, message: string, errorType: string) => {
    trackInquiryValidationError({ field_name: name, error_type: errorType, vehicle_type: vehicleType });
    setSubmitErrors({ [name]: message });
    toast.error(message);
    const el = fieldRefs.current[name];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => {
        try {
          (el as HTMLElement).focus({ preventScroll: true });
        } catch {
          /* noop */
        }
      }, 250);
    }
  };
  const errorCn = (name: string) =>
    submitErrors[name] ? "border-destructive ring-2 ring-destructive/30 bg-destructive/5" : "";
  const errorHint = (name: string) =>
    submitErrors[name] ? (
      <p id={`${name}-error`} className="mt-1 text-sm font-semibold text-destructive" role="alert">
        {submitErrors[name]}
      </p>
    ) : null;

  // Lokaler Phone-State entkoppelt schnelle Eingabe von schweren Rechnungen
  // (Preis-Memo, Draft-Save, Upsell). Sync in formData über deferred value.
  const [phoneLocal, setPhoneLocal] = useState("");
  const deferredPhone = useDeferredValue(phoneLocal);
  useEffect(() => {
    setFormData((p) => (p.phone === deferredPhone ? p : { ...p, phone: deferredPhone }));
  }, [deferredPhone]);

  const mobileDetailsRef = useRef<HTMLButtonElement | null>(null);
  const handleMobileDetailsOpen = (open: boolean) => {
    setMobileDetailsOpen(open);
  };



  // Track form open
  useEffect(() => {
    trackEvent({
      eventType: "inquiry_form_open",
      eventData: {
        vehicle_type: vehicleType,
        vehicle_label: vehicleLabel,
        vehicle_id: vehicleId ?? null,
        source: source ?? variant,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Consume hero quick-start prefill (Datum bereits im Hero gewählt)
  useEffect(() => {
    import("@/lib/inquiryPrefill").then(({ consumeInquiryPrefill }) => {
      const prefill = consumeInquiryPrefill();
      if (!prefill?.pickupDate) return;
      const parseLocalDate = (iso: string): Date | null => {
        const s = iso.slice(0, 10);
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
        if (!m) return null;
        const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return isNaN(dt.getTime()) ? null : dt;
      };
      const d = parseLocalDate(prefill.pickupDate);
      if (!d) return;
      let r: Date | null = null;
      if (prefill.returnDate) {
        r = parseLocalDate(prefill.returnDate);
      }
      setFormData((prev) => ({
        ...prev,
        pickupDate: d,
        returnDate: r ?? prev.returnDate ?? addDays(d, 1),
      }));
      trackEvent({
        eventType: "hero_quickstart_prefill_applied",
        eventData: { source: prefill.source, target: "inquiry_form" },
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track field focus once per field (drop-off analysis)
  const focusedFieldsRef = useRef<Set<string>>(new Set());
  const formOpenedAtRef = useRef<number>(Date.now());
  const firstFocusReportedRef = useRef(false);
  const trackFieldFocus = (fieldName: string) => {
    if (focusedFieldsRef.current.has(fieldName)) return;
    focusedFieldsRef.current.add(fieldName);
    setInquirySheetFieldFocus(fieldName);
    if (!firstFocusReportedRef.current) {
      firstFocusReportedRef.current = true;
      trackInquiryTimeToFirstFocus({
        seconds: Math.round((Date.now() - formOpenedAtRef.current) / 1000),
        field_name: fieldName,
        vehicle_type: vehicleType,
        vehicle_id: vehicleId ?? null,
        viewport_h: typeof window !== "undefined" ? window.innerHeight : 0,
      });
    }
    trackEvent({
      eventType: "form_field_focus",
      eventData: {
        field_name: fieldName,
        step,
        vehicle_type: vehicleType,
        source: source ?? variant,
      },
    });
  };

  // Track abandonment: when component unmounts with partial data and no submission
  const submittedRef = useRef(false);
  useEffect(() => {
    return () => {
      if (submittedRef.current) return;
      const hasAnyInput =
        formData.name ||
        formData.phone ||
        formData.email ||
        formData.pickupDate;
      if (!hasAnyInput) return;
      trackEvent({
        eventType: "form_abandoned",
        eventData: {
          step,
          had_dates: !!(formData.pickupDate && formData.returnDate),
          had_phone: !!formData.phone,
          had_email: !!formData.email,
          had_name: !!formData.name,
          had_privacy: formData.privacy,
          vehicle_type: vehicleType,
          source: source ?? variant,
        },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save Draft (nur mit Consent + Kontaktdaten)
  useInquiryDraft({
    customer_name: formData.name || null,
    customer_email: formData.email || null,
    customer_phone: formData.phone || null,
    customer_company: formData.company || null,
    start_date: formData.pickupDate ? format(formData.pickupDate, "yyyy-MM-dd") : null,
    end_date: formData.returnDate ? format(formData.returnDate, "yyyy-MM-dd") : null,
    vehicle_id: isAdviceRequest ? null : vehicleId ?? null,
    vehicle_label: vehicleLabel,
    vehicle_type: vehicleType,
    temperature_requirement: isKuehl ? (formData.temperature || null) : null,
    cargo_description: formData.usage || null,
    delivery_enabled: formData.deliveryEnabled,
    delivery_address: formData.deliveryEnabled ? (formData.deliveryAddress || null) : null,
    extra_km: (parseInt(formData.extraKm) || 0) > 0 ? parseInt(formData.extraKm) : null,
    source_page: typeof window !== "undefined" ? window.location.pathname : null,
  });

  // Sicheres Reset der Temperatur, wenn Fahrzeug nur Frische kann (kein Tiefkühl).
  useEffect(() => {
    if (isKuehl && isFreshOnlyCoolingVehicle(vehicleLabel) && formData.temperature === "-18 bis -25 °C") {
      setFormData((p) => ({ ...p, temperature: "" }));
    }
  }, [isKuehl, vehicleLabel, formData.temperature]);

  useEffect(() => {
    const table = isKuehl ? "pricing" : "transporter_pricing";
    supabase.from(table).select("*").order("display_order").then(({ data }) => {
      if (data) setPricingItems(data as PricingItem[]);
    });
  }, [isKuehl]);

  useEffect(() => {
    if (!vehicleId || isAdviceRequest) {
      setTechnicalSpecs([]);
      return;
    }
    supabase
      .from("vehicles")
      .select("technical_specs")
      .eq("id", vehicleId)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.technical_specs) {
          setTechnicalSpecs(data.technical_specs as unknown as TechnicalSpec[]);
        } else {
          setTechnicalSpecs([]);
        }
      });
  }, [vehicleId, isAdviceRequest]);


  const validateField = (name: keyof typeof fieldValidation, value: string) => {
    try {
      fieldValidation[name].parse(value);
      setFieldValid((p) => ({ ...p, [name]: true }));
      setFieldErrors((p) => ({ ...p, [name]: null }));
    } catch (err) {
      if (err instanceof z.ZodError) {
        setFieldValid((p) => ({ ...p, [name]: false }));
        setFieldErrors((p) => ({ ...p, [name]: err.errors[0].message }));
      }
    }
  };

  const handleBlur = (name: keyof typeof fieldValidation, value: string) => {
    setTouched((p) => ({ ...p, [name]: true }));
    if (value.trim()) validateField(name, value);
  };

  const extraKmNum = parseInt(formData.extraKm) || 0;
  const extraKmCost = extraKmNum * EXTRA_KM_RATE;

  // Filter pricing rows to the selected vehicle when a vehicleId is provided.
  // The transporter_pricing table holds rows for multiple vehicles; without
  // filtering, .find() can return prices belonging to a different vehicle.
  const vehiclePricing = useMemo(() => {
    if (!vehicleId) return pricingItems;
    const own = pricingItems.filter((p) => p.vehicle_id === vehicleId);
    return own.length > 0 ? own : pricingItems;
  }, [pricingItems, vehicleId]);




  const priceCalc = useMemo(() => {
    if (!formData.pickupDate || !formData.returnDate) return null;
    const days = differenceInDays(formData.returnDate, formData.pickupDate);
    if (days <= 0) return null;

    // Robuste Tier-Erkennung: primär über rental_days, Name nur als Fallback.
    const nameLc = (p: PricingItem) => (p.name || "").toLowerCase();
    const dailyRate =
      vehiclePricing.find((p) => p.rental_days === 1) ||
      vehiclePricing.find((p) => nameLc(p).includes("tag") && !nameLc(p).includes("woche"));
    const weekendRate =
      vehiclePricing.find((p) => p.rental_days === 2) ||
      vehiclePricing.find((p) => nameLc(p).includes("wochenende"));
    const weeklyRate =
      vehiclePricing.find((p) => p.rental_days === 7) ||
      vehiclePricing.find((p) => nameLc(p).includes("woche") && !nameLc(p).includes("wochenende"));
    const monthlyRate =
      vehiclePricing.find((p) => (p.rental_days ?? 0) >= 28) ||
      vehiclePricing.find((p) => nameLc(p).includes("monat"));
    if (!dailyRate) return null;

    let basePrice = 0;
    let includedKm = 0;
    const breakdown: string[] = [];
    let weekendApplied = false;

    const coversWeekend = (() => {
      const d = new Date(formData.pickupDate!);
      const end = new Date(formData.returnDate!);
      while (d <= end) {
        if (d.getDay() === 0 || d.getDay() === 6) return true;
        d.setDate(d.getDate() + 1);
      }
      return false;
    })();

    const calcShort = (d: number) => {
      if (coversWeekend && weekendRate && d === (weekendRate.rental_days || 3)) {
        if (weekendRate.price_amount <= d * dailyRate.price_amount) {
          weekendApplied = true;
          return { price: weekendRate.price_amount, km: weekendRate.included_km || 0, label: `1× Wochenende (${d} Tage) × ${formatPrice(weekendRate.price_amount)} €` };
        }
      }
      if (coversWeekend && weekendRate && d > (weekendRate.rental_days || 3) && d < 7) {
        const wd = weekendRate.rental_days || 3;
        const rem = d - wd;
        const mix = weekendRate.price_amount + rem * dailyRate.price_amount;
        if (mix < d * dailyRate.price_amount) {
          weekendApplied = true;
          return { price: mix, km: (weekendRate.included_km || 0) + rem * (dailyRate.included_km || 0), label: `Wochenende + ${rem} Tag(e)` };
        }
      }
      return { price: d * dailyRate.price_amount, km: d * (dailyRate.included_km || 0), label: `${d} Tag(e) × ${formatPrice(dailyRate.price_amount)} €` };
    };

    if (days >= 28 && monthlyRate) {
      const m = Math.floor(days / 28);
      const rem = days % 28;
      basePrice = m * monthlyRate.price_amount;
      includedKm = m * (monthlyRate.included_km || 0);
      breakdown.push(`${m} Monat(e) × ${formatPrice(monthlyRate.price_amount)} €`);
      if (rem >= 7 && weeklyRate) {
        const w = Math.floor(rem / 7);
        const ed = rem % 7;
        basePrice += w * weeklyRate.price_amount;
        includedKm += w * (weeklyRate.included_km || 0);
        breakdown.push(`${w} Woche(n) × ${formatPrice(weeklyRate.price_amount)} €`);
        if (ed > 0) { const s = calcShort(ed); basePrice += s.price; includedKm += s.km; breakdown.push(s.label); }
      } else if (rem > 0) { const s = calcShort(rem); basePrice += s.price; includedKm += s.km; breakdown.push(s.label); }
    } else if (days >= 7 && weeklyRate) {
      const w = Math.floor(days / 7);
      const ed = days % 7;
      basePrice = w * weeklyRate.price_amount;
      includedKm = w * (weeklyRate.included_km || 0);
      breakdown.push(`${w} Woche(n) × ${formatPrice(weeklyRate.price_amount)} €`);
      if (ed > 0) { const s = calcShort(ed); basePrice += s.price; includedKm += s.km; breakdown.push(s.label); }
    } else {
      const s = calcShort(days);
      basePrice = s.price;
      includedKm = s.km;
      breakdown.push(s.label);
    }

    const nettoTotal = basePrice + extraKmCost;
    const tax = Math.round(nettoTotal * 0.19 * 100) / 100;
    return { days, basePrice, includedKm, breakdown, tax, total: nettoTotal + tax, weekendApplied, nettoTotal };
  }, [formData.pickupDate, formData.returnDate, vehiclePricing, extraKmCost]);

  const upsellHint = useMemo(() => {
    if (!priceCalc || !formData.pickupDate) return null;
    const days = priceCalc.days;
    const nameLc = (p: PricingItem) => (p.name || "").toLowerCase();
    const dailyRate =
      vehiclePricing.find((p) => p.rental_days === 1) ||
      vehiclePricing.find((p) => nameLc(p).includes("tag") && !nameLc(p).includes("woche"));
    const weekendRate =
      vehiclePricing.find((p) => p.rental_days === 2) ||
      vehiclePricing.find((p) => nameLc(p).includes("wochenende"));
    const weeklyRate =
      vehiclePricing.find((p) => p.rental_days === 7) ||
      vehiclePricing.find((p) => nameLc(p).includes("woche") && !nameLc(p).includes("wochenende"));
    const monthlyRate =
      vehiclePricing.find((p) => (p.rental_days ?? 0) >= 28) ||
      vehiclePricing.find((p) => nameLc(p).includes("monat"));
    if (!dailyRate) return null;

    if (days >= 21 && days <= 27 && monthlyRate) {
      const effectiveDaily = Math.floor(monthlyRate.price_amount / 28);
      return {
        suggestedDays: 28,
        message: `Mit 28 Tagen zahlen Sie pauschal ${formatPrice(monthlyRate.price_amount)} € = nur ${formatPrice(effectiveDaily)} €/Tag.`,
      };
    }

    if (days >= 4 && days <= 6 && weeklyRate) {
      const effectiveDaily = Math.floor(weeklyRate.price_amount / 7);
      const currentDailyEquivalent = priceCalc.basePrice / days;
      const dailyKm = dailyRate.included_km || 0;
      const weeklyKm = weeklyRate.included_km || 0;
      const extraKm = Math.max(0, weeklyKm - days * dailyKm);
      if (effectiveDaily < currentDailyEquivalent) {
        return {
          suggestedDays: 7,
          message: `Bei 7 Tagen zahlen Sie pauschal ${formatPrice(weeklyRate.price_amount)} € = ${formatPrice(effectiveDaily)} €/Tag${extraKm > 0 ? ` und erhalten ${extraKm} km zusätzlich inklusive` : ""}.`,
        };
      }
    }

    if (weekendRate && !priceCalc.weekendApplied) {
      const targetDays = weekendRate.rental_days || 3;
      if (targetDays - days >= 1) {
        const targetEnd = addDays(formData.pickupDate, targetDays);
        let coversWeekend = false;
        const d = new Date(formData.pickupDate);
        while (d <= targetEnd) {
          if (d.getDay() === 0 || d.getDay() === 6) { coversWeekend = true; break; }
          d.setDate(d.getDate() + 1);
        }
        const wouldCost = targetDays * dailyRate.price_amount;
        if (coversWeekend && weekendRate.price_amount < wouldCost) {
          return {
            suggestedDays: targetDays,
            message: `+${targetDays - days} Tag(e) und Sie zahlen nur ${formatPrice(weekendRate.price_amount)} € statt ${formatPrice(wouldCost)} € (Wochenend-Paket).`,
          };
        }
      }
    }

    return null;
  }, [priceCalc, vehiclePricing, formData.pickupDate]);

  const applyUpsell = (suggestedDays: number) => {
    if (!formData.pickupDate) return;
    const newReturn = addDays(formData.pickupDate, suggestedDays);
    const diff = suggestedDays - (priceCalc?.days || 0);
    setFormData((p) => ({ ...p, returnDate: newReturn }));
    trackCTAClick("upsell_apply", source ?? "inquiry_form", `+${diff}_tage`);
    toast.success(t("inquiryForm.upsellExtended", { days: suggestedDays }));
  };

  const canProceedToStep2 = !!formData.pickupDate || dateUnknown;

  const goToStep2 = (datesKnown: boolean) => {
    trackEvent({
      eventType: "form_step_complete",
      eventData: {
        from_step: 1,
        to_step: 2,
        dates_known: datesKnown,
        vehicle_type: vehicleType,
        source: source ?? variant,
      },
    });
    setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent, opts?: { mobileCompact?: boolean }) => {
    e.preventDefault();
    const rateCheck = isClientRateLimited();
    if (rateCheck.limited) {
      toast.error(`Zu viele Anfragen. Bitte in ${Math.ceil(rateCheck.retryInSeconds / 60)} Min. erneut versuchen.`);
      return;
    }

    setSubmitErrors({});

    if (opts?.mobileCompact) {
      // Mobil und Desktop: Name + (Telefon ODER E-Mail). Der Termin darf noch offen sein.
      if (!formData.name || formData.name.trim().length < 2) {
        failSubmitValidation("name", t("inquiryForm.requiredName"), "missing_or_too_short");
        return;
      }
      const hasPhone = formData.phone.trim().length >= 6;
      const hasEmail = /\S+@\S+\.\S+/.test(formData.email);
      if (contactPreference === "phone" && !hasPhone) {
        failSubmitValidation("phone", t("inquiryForm.requiredPhone"), "missing_phone");
        return;
      }
      if (contactPreference === "email" && !hasEmail) {
        failSubmitValidation("email", t("inquiryForm.requiredEmail"), "missing_email");
        return;
      }
      if (formData.email && !hasEmail) {
        failSubmitValidation("email", t("inquiryForm.invalidEmailOptional"), "invalid_format");
        return;
      }
      if (!formData.pickupDate && !dateUnknown) {
        failSubmitValidation("pickupDate", t("inquiryForm.requiredPickupDate"), "missing");
        return;
      }
    } else {
      // Desktop: dieselbe Pflichtlogik wie mobil.
      if (!formData.name || formData.name.trim().length < 2) {
        failSubmitValidation("name", t("inquiryForm.requiredName"), "missing_or_too_short");
        return;
      }
      const hasPhone = formData.phone.trim().length >= 6;
      const hasEmail = /\S+@\S+\.\S+/.test(formData.email);
      if (contactPreference === "phone" && !hasPhone) {
        failSubmitValidation("phone", t("inquiryForm.requiredPhone"), "missing_phone");
        return;
      }
      if (contactPreference === "email" && !hasEmail) {
        failSubmitValidation("email", t("inquiryForm.requiredEmail"), "missing_email");
        return;
      }
      if (!formData.pickupDate && !dateUnknown) {
        // Datum liegt in Schritt 1 – dorthin zurückspringen und Feld markieren.
        setStep(1);
        window.setTimeout(() => failSubmitValidation("pickupDate", t("inquiryForm.requiredPickupDate"), "missing"), 60);
        return;
      }
      // E-Mail nur validieren wenn ausgefüllt.
      if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
        failSubmitValidation(
          "email",
          t("inquiryForm.invalidEmailOptional"),
          "invalid_format",
        );
        return;
      }
    }


    setIsLoading(true);
    try {
      const nameTrim = formData.name.trim();
      const displayName = nameTrim;
      const vehicleLabelSafe =
        (typeof vehicleLabel === "string" && vehicleLabel.trim()) ||
        "nicht ausgewählt (Fahrzeugliste konnte nicht geladen werden – bitte Kunde telefonisch klären)";
      if (!vehicleLabel || !vehicleLabel.trim()) {
        console.warn("[InquiryForm] Missing vehicleLabel at submit", { vehicleId, vehicleType });
        trackEvent({
          eventType: "inquiry_missing_vehicle_label",
          eventData: { vehicle_id: vehicleId ?? null, vehicle_type: vehicleType, source: source ?? variant },
        });
      }
      const messageLines = [
        `Fahrzeug: ${vehicleLabelSafe}`,
        !formData.pickupDate ? "Mietzeitraum: noch offen / wird telefonisch geklärt" : "",
        priceCalc ? `Geschätzte Kosten: ${priceCalc.nettoTotal.toFixed(2)} € netto` : "",
        extraKmNum > 0 ? `Extra-Kilometer: ${extraKmNum} km (${formatPrice(extraKmCost)} €)` : "",
        formData.deliveryEnabled ? `Anlieferung gewünscht: ${formData.deliveryAddress || "Adresse nicht angegeben"}` : "",
        opts?.mobileCompact && !formData.email ? "Kontakt: bevorzugt telefonisch (Kurzformular Mobile)" : "",
      ].filter(Boolean).join("\n");

      const sourcePage = typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.hash || ""}`.slice(0, 500)
        : null;
      const inquiryId = crypto.randomUUID();
      const { error } = await supabase.from("inquiries").insert([{
        id: inquiryId,
        customer_name: displayName,
        customer_email: formData.email || "",
        customer_phone: formData.phone || "",
        customer_company: formData.company || null,
        start_date: formData.pickupDate ? format(formData.pickupDate, "yyyy-MM-dd") : null,
        end_date: formData.returnDate ? format(formData.returnDate, "yyyy-MM-dd") : null,
        temperature_requirement: isKuehl ? (formData.temperature || null) : null,
        cargo_description: formData.usage || null,
        delivery_location: formData.deliveryEnabled ? (formData.deliveryAddress || "Anlieferung gewünscht") : null,
        message: messageLines,
        source: source ?? "website",
        referrer: sourcePage,
        status: "new" as const,
        vehicle_id: isAdviceRequest ? null : vehicleId ?? null,
        vehicle_label: vehicleLabelSafe,
      } as any]);
      if (error) throw error;

      if (inquiryId) {
        supabase.functions.invoke("notify-new-inquiry", {
          body: {
            inquiry_id: inquiryId,
            vehicle_label: vehicleLabelSafe,
            source_page: sourcePage,
            extra_km: extraKmNum > 0 ? extraKmNum : undefined,
            extra_km_cost: extraKmNum > 0 ? extraKmCost : undefined,
            delivery_enabled: formData.deliveryEnabled || undefined,
            delivery_address: formData.deliveryEnabled ? (formData.deliveryAddress || "Adresse nicht angegeben") : undefined,
            price_breakdown: priceCalc ? {
              days: priceCalc.days,
              breakdown_lines: priceCalc.breakdown,
              base_price: priceCalc.basePrice,
              extra_km_cost: extraKmCost,
              included_km: priceCalc.includedKm,
              netto_total: priceCalc.nettoTotal,
              tax: priceCalc.tax,
              brutto_total: priceCalc.total,
              weekend_applied: priceCalc.weekendApplied,
            } : undefined,
          },
        }).then(({ error: fnError }) => {
          if (fnError) {
            console.error("notify-new-inquiry failed:", fnError);
            toast.warning("Bestätigungs-E-Mail konnte nicht gesendet werden — wir melden uns trotzdem persönlich.");
          }
        }).catch((err) => {
          console.error("notify-new-inquiry threw:", err);
          toast.warning("Bestätigungs-E-Mail konnte nicht gesendet werden — wir melden uns trotzdem persönlich.");
        });
      }

      if (isCoolingInquiry(typeof window !== "undefined" ? window.location.pathname : null)) {
        notifyExternalInquiry({
          name: displayName,
          email: formData.email,
          phone: formData.phone || undefined,
          firma: formData.company || undefined,
          vehicle: vehicleLabelSafe,
          start: formData.pickupDate,
          end: formData.returnDate,
          price: priceCalc?.nettoTotal,
          includedKm: priceCalc?.includedKm,
          extraKm: extraKmNum > 0 ? extraKmNum : undefined,
          anlieferung: !!formData.deliveryEnabled,
          anlieferungText: formData.deliveryAddress || undefined,
          temperatur: formData.temperature || undefined,
          message: formData.usage || undefined,
        });
      }

      trackFormSubmission("inquiry_form_request", {
        vehicleType,
        pickupDate: formData.pickupDate ? format(formData.pickupDate, "yyyy-MM-dd") : undefined,
        returnDate: formData.returnDate ? format(formData.returnDate, "yyyy-MM-dd") : undefined,
        estimatedPrice: priceCalc?.nettoTotal,
        extraKm: extraKmNum,
        delivery: formData.deliveryEnabled,
        source: source ?? variant,
      });
      trackConversion("contact_form");
      recordSubmission();
      setSuccessContactMethod(contactPreference);

      setFormData({ pickupDate: null, returnDate: null, extraKm: "", deliveryEnabled: false, deliveryAddress: "", temperature: "", usage: "", name: "", phone: "", email: "", company: "", privacy: false });
      setPhoneLocal("");
      setTouched({});
      setFieldValid({});
      setFieldErrors({});
      setStep(1);
      setDateUnknown(false);
      setContactPreference("phone");
      setSuccessOpen(true);
      submittedRef.current = true;
      clearInquiryDraft();
    } catch (err: any) {
      try {
        supabase.functions.invoke("notify-inquiry-error", {
          body: {
            alert_kind: "Submit-Fehler im Anfrageformular",
            error_type: err?.code || err?.name || "submit_error",
            error_message: String(err?.message || err || "unbekannt").slice(0, 2000),
            page: typeof window !== "undefined" ? window.location.pathname : undefined,
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
            contact: {
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
            },
          },
        }).catch((e) => console.warn("alert send failed:", e));
      } catch (e) { console.warn("alert dispatch threw:", e); }
      toast.error("Fehler beim Senden. Bitte erneut versuchen.");
    } finally {
      setIsLoading(false);
    }
  };

  const inputCn = (name: keyof typeof fieldValidation) => {
    if (!touched[name]) return "";
    if (fieldValid[name]) return "border-green-500 focus-visible:ring-green-500";
    if (fieldErrors[name]) return "border-destructive focus-visible:ring-destructive";
    return "";
  };

  const renderFieldIcon = (name: string) => {
    if (!touched[name]) return null;
    if (fieldValid[name]) return <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-green-500" />;
    if (fieldErrors[name]) return <AlertCircle className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-destructive" />;
    return null;
  };

  const priceSummaryCompact = priceCalc ? (
    <div className="bg-secondary/30 border border-border rounded-lg p-3 space-y-1 text-xs">
      <div className="flex items-center gap-1.5 text-primary font-semibold text-sm">
        <Calculator className="h-3.5 w-3.5" />
        {priceCalc.days} Tag{priceCalc.days !== 1 ? "e" : ""} – {formatPrice(priceCalc.total)} € brutto
      </div>
      <div className="text-muted-foreground">Netto: {formatPrice(priceCalc.nettoTotal)} € · MwSt.: {formatPrice(priceCalc.tax)} €</div>
    </div>
  ) : null;

  // Kompaktester Daily-Preis für dieses Fahrzeug (Header-Anzeige „ab X €/Tag netto")
  const nameLcAll = (p: PricingItem) => (p.name || "").toLowerCase();
  const dailyRateForHeader =
    vehiclePricing.find((p) => p.rental_days === 1) ||
    vehiclePricing.find((p) => nameLcAll(p).includes("tag") && !nameLcAll(p).includes("woche"));
  const monthlyRateForHeader =
    vehiclePricing.find((p) => (p.rental_days ?? 0) >= 28) ||
    vehiclePricing.find((p) => nameLcAll(p).includes("monat"));
  const headerFromPrice = isAdviceRequest
    ? null
    : monthlyRateForHeader
    ? Math.floor(monthlyRateForHeader.price_amount / 30)
    : dailyRateForHeader?.price_amount ?? null;

  const mobileFormBody = (
    <form onSubmit={(e) => handleSubmit(e, { mobileCompact: true })} className="p-3 pb-2 min-w-0 relative space-y-2.5">
      {/* Header (kompakt) */}
      <div className="flex items-center gap-2">
        <div className="bg-accent/10 rounded-md p-1.5 shrink-0">
          <Truck className="h-4 w-4 text-accent" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold leading-tight break-words">{vehicleLabel}</h3>
          {headerFromPrice != null && (
            <p className="text-[11px] font-semibold text-primary leading-tight">
              {t("inquiryForm.fromPrice", { price: formatPrice(headerFromPrice) })}
            </p>
          )}
        </div>
      </div>

      {/* Name (Pflicht) */}
      <h4 className="text-sm font-bold text-primary">{t("inquiryForm.mobileContactStep")}</h4>
      <div>
        <Label htmlFor="mobile-name" className="text-sm font-medium">
          {t("inquiryForm.nameLabel")} <span className="text-destructive">*</span>
          {submitErrors.name && <span className="text-destructive font-semibold">{t("inquiryForm.fillIn")}</span>}
        </Label>
        <Input
          ref={registerField("name")}
          id="mobile-name"
          disabled={isLoading}
          autoComplete="name"
          aria-required="true"
          aria-invalid={!!submitErrors.name}
          aria-describedby={submitErrors.name ? "name-error" : undefined}
          value={formData.name}
          maxLength={100}
          onFocusCapture={() => trackFieldFocus("name")}
          onChange={(e) => { clearSubmitError("name"); setFormData((p) => ({ ...p, name: e.target.value })); }}
          placeholder={t("inquiryForm.namePlaceholder")}
          className={cn("mt-1 h-11 text-base", errorCn("name"))}
        />
        {errorHint("name")}
      </div>

      {/* Mindestens ein Kontaktweg */}
      <RadioGroup value={contactPreference} onValueChange={(value) => {
        const preference = value as "phone" | "email";
        setContactPreference(preference);
        clearSubmitError(preference === "phone" ? "email" : "phone");
      }} className="grid grid-cols-2 gap-2" aria-label={t("inquiryForm.contactPreference") }>
        <label htmlFor="mobile-contact-phone" className={cn("flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm font-semibold", contactPreference === "phone" ? "border-primary bg-primary/5 text-primary" : "border-border")}>
          <RadioGroupItem id="mobile-contact-phone" value="phone" />
          {t("inquiryForm.phoneLabel")}
        </label>
        <label htmlFor="mobile-contact-email" className={cn("flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm font-semibold", contactPreference === "email" ? "border-primary bg-primary/5 text-primary" : "border-border")}>
          <RadioGroupItem id="mobile-contact-email" value="email" />
          {t("inquiryForm.emailLabel")}
        </label>
      </RadioGroup>
      <div>
        <Label htmlFor="mobile-phone" className="text-sm font-medium">
          {t("inquiryForm.phoneLabel")} {contactPreference === "phone" && <span className="text-destructive">*</span>}
          {submitErrors.phone && <span className="text-destructive font-semibold">{t("inquiryForm.fillIn")}</span>}
        </Label>
        <Input
          ref={registerField("phone")}
          id="mobile-phone"
          disabled={isLoading}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          aria-invalid={!!submitErrors.phone}
          aria-describedby={submitErrors.phone ? "phone-error" : undefined}
          value={phoneLocal}
          maxLength={40}
          onFocusCapture={() => trackFieldFocus("phone")}
          onChange={(e) => { clearSubmitError("phone"); setPhoneLocal(e.target.value); }}
          placeholder={t("inquiryForm.phonePlaceholder")}
          className={cn("mt-1 h-11 text-base", errorCn("phone"))}
        />
        {errorHint("phone")}
      </div>
      <div>
        <Label htmlFor="mobile-email" className="text-sm font-medium">
          {t("inquiryForm.emailLabel")} {contactPreference === "email" && <span className="text-destructive">*</span>}
        </Label>
        <Input
          ref={registerField("email")}
          id="mobile-email"
          disabled={isLoading}
          type="email"
          inputMode="email"
          autoComplete="email"
          aria-invalid={!!submitErrors.email}
          aria-describedby={submitErrors.email ? "email-error" : undefined}
          value={formData.email}
          maxLength={255}
          onFocusCapture={() => trackFieldFocus("email")}
          onChange={(e) => { clearSubmitError("phone"); clearSubmitError("email"); setFormData((p) => ({ ...p, email: e.target.value })); }}
          placeholder={t("inquiryForm.emailPlaceholder")}
          className={cn("mt-1 h-11 text-base", errorCn("email"))}
        />
        {errorHint("email")}
      </div>

      {/* Mietbeginn + Mietende nebeneinander */}
      <h4 className="text-sm font-bold text-primary">{t("inquiryForm.mobileDateStep")}</h4>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs font-medium">
            {t("inquiryForm.pickupDateLabel")}
          </Label>
          <Popover open={pickupPopoverOpen} onOpenChange={setPickupPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                ref={registerField("pickupDate") as React.Ref<HTMLButtonElement>}
                type="button"
                variant="outline"
                aria-invalid={!!submitErrors.pickupDate}
                aria-describedby={submitErrors.pickupDate ? "pickupDate-error" : undefined}
                className={cn(
                  "w-full mt-1 justify-start text-left text-sm font-normal h-11 px-2.5",
                  !formData.pickupDate && "text-muted-foreground",
                  errorCn("pickupDate"),
                )}
                onClick={() => trackFieldFocus("pickupDate")}
              >
                <CalendarIcon className="mr-1.5 h-4 w-4 shrink-0" />
                <span className="truncate">
                  {formData.pickupDate ? format(formData.pickupDate, isEnglish ? "dd/MM/yy" : "dd.MM.yy", { locale: de }) : t("inquiryForm.datePlaceholder")}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 z-[60]" align="start">
              <Calendar
                mode="single"
                selected={formData.pickupDate || undefined}
                onSelect={(d) => {
                  clearSubmitError("pickupDate");
                  setFormData((p) => ({ ...p, pickupDate: d || null }));
                  if (d) setDateUnknown(false);
                  setPickupPopoverOpen(false);
                }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
                locale={de}
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
          {errorHint("pickupDate")}
        </div>
        <div>
          <Label className="text-xs font-medium">{t("inquiryForm.returnDateLabel")}</Label>
          <Popover open={returnPopoverOpen} onOpenChange={setReturnPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  "w-full mt-1 justify-start text-left text-sm font-normal h-11 px-2.5",
                  !formData.returnDate && "text-muted-foreground",
                )}
                onClick={() => trackFieldFocus("returnDate")}
              >
                <CalendarIcon className="mr-1.5 h-4 w-4 shrink-0" />
                <span className="truncate">
                  {formData.returnDate ? format(formData.returnDate, isEnglish ? "dd/MM/yy" : "dd.MM.yy", { locale: de }) : t("inquiryForm.datePlaceholder")}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 z-[60]" align="start">
              <Calendar
                mode="single"
                selected={formData.returnDate || undefined}
                onSelect={(d) => {
                  setFormData((p) => ({ ...p, returnDate: d || null }));
                  setReturnPopoverOpen(false);
                }}
                disabled={(d) => formData.pickupDate ? d <= formData.pickupDate : d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
                locale={de}
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id="mobile-date-unknown"
          checked={dateUnknown}
          onCheckedChange={(checked) => {
            const next = checked === true;
            setDateUnknown(next);
            clearSubmitError("pickupDate");
            if (next) setFormData((p) => ({ ...p, pickupDate: null, returnDate: null }));
          }}
        />
        <label htmlFor="mobile-date-unknown" className="min-h-11 flex items-center text-sm font-medium cursor-pointer">
          {t("inquiryForm.dateUnknown")}
        </label>
      </div>

      {/* Optionale Details */}
      <Collapsible open={mobileDetailsOpen} onOpenChange={handleMobileDetailsOpen}>
        <CollapsibleTrigger asChild>
          <button
            ref={mobileDetailsRef}
            type="button"
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-border bg-secondary/30 hover:bg-secondary/50 transition-colors text-left"
          >
            <span className="text-xs font-medium text-foreground leading-snug">
              {mobileDetailsOpen ? t("inquiryForm.collapseDetails") : t("inquiryForm.addDetails")}
              <span className="text-muted-foreground font-normal">{t("inquiryForm.addDetailsHint")}</span>
            </span>
            <ChevronDown
              className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", mobileDetailsOpen && "rotate-180")}
              strokeWidth={1.5}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          <div className="pt-2 space-y-2">
            <div>
              <Label className="text-[11px] font-medium">{t("inquiryForm.extraKmLabel")}</Label>
              <div className="relative mt-0.5">
                <Input
                  disabled={isLoading}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={50}
                  value={formData.extraKm}
                  onFocusCapture={() => trackFieldFocus("extraKm")}
                  onChange={(e) => setFormData((p) => ({ ...p, extraKm: e.target.value }))}
                  placeholder={t("inquiryForm.extraKmPlaceholder")}
                  className="h-9 text-sm pr-10"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">km</span>
              </div>
              {priceCalc && priceCalc.includedKm > 0 && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  {t("inquiryForm.includedKmInfo", { km: priceCalc.includedKm })}
                </p>
              )}
              {extraKmNum > 0 && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  {t("inquiryForm.extraKmCost", { km: extraKmNum, rate: formatPrice(EXTRA_KM_RATE), cost: formatPrice(extraKmCost) })}
                </p>
              )}
              {priceCalc && (
                <p className="text-[10px] font-medium text-primary mt-1">
                  {t("inquiryForm.newTotal", { netto: formatPrice(priceCalc.nettoTotal), brutto: formatPrice(priceCalc.total) })}
                </p>
              )}
            </div>
            <div>
              <Label className="text-[11px] font-medium">{t("inquiryForm.messageLabel")}</Label>
              <Input
                disabled={isLoading}
                value={formData.usage}
                onChange={(e) => setFormData((p) => ({ ...p, usage: e.target.value }))}
                placeholder={isKuehl ? t("inquiryForm.messagePlaceholderCooling") : t("inquiryForm.messagePlaceholderTransporter")}
                className="mt-0.5 h-9 text-sm"
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Preisvorschau (einzeilig, ausgeblendet bei geöffneten Details) */}
      {priceCalc && !mobileDetailsOpen && (
        <div className="rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1.5 text-[11px] font-medium text-primary flex items-center justify-between gap-2 flex-wrap">
          <span>{t("inquiryForm.daysTotal", { days: priceCalc.days, netto: formatPrice(priceCalc.nettoTotal) })}</span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {priceCalc.includedKm > 0 && (
              <span className="inline-flex items-center gap-0.5 bg-success/10 text-success border border-success/20 rounded px-1.5 py-0.5 text-[10px] font-semibold">
                <Check className="h-2.5 w-2.5" />
                {t("inquiryForm.includedKmShort", { km: priceCalc.includedKm })}
              </span>
            )}
            {extraKmNum > 0 && (
              <span className="inline-flex items-center bg-background border border-border rounded px-1.5 py-0.5 text-[10px] text-foreground">
                {t("inquiryForm.extraKmShort", { km: extraKmNum, cost: formatPrice(extraKmCost) })}
              </span>
            )}
            <span className="text-muted-foreground font-normal">{t("inquiryForm.grossShort", { amount: formatPrice(priceCalc.total) })}</span>
          </div>
        </div>
      )}
      {!priceCalc && formData.pickupDate && !formData.returnDate && (
        <p className="text-[10px] text-muted-foreground text-center">
          {t("inquiryForm.includedKmAfterReturn")}
        </p>
      )}

      {/* Sticky Submit */}
      <div className="sticky bottom-0 -mx-3 px-3 py-2 mt-1 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85 border-t border-border space-y-2 z-10 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <Button
          type="submit"
          className="w-full h-11 gradient-primary hover:opacity-90"
          disabled={isLoading}
          aria-busy={isLoading}
        >
          <span className="flex items-center justify-center gap-2">
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            <span className="font-semibold">{isLoading ? t("inquiryForm.sending") : t("inquiryForm.submit")}</span>
          </span>
        </Button>
        {!mobileDetailsOpen ? (
          <div className="grid grid-cols-2 gap-2">
            <a
              href={`https://wa.me/${PHONE_WHATSAPP}?text=${encodeURIComponent(`Hallo, ich interessiere mich für den ${vehicleLabel}.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCTAClick("whatsapp", "inquiry_sheet_mobile_header", "WhatsApp")}
              className="inline-flex items-center justify-center gap-1.5 h-9 rounded-md bg-[hsl(145,65%,39%)] hover:bg-[hsl(145,65%,33%)] text-white text-xs font-semibold"
            >
              <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
              {t("inquiryForm.whatsapp")}
            </a>
            <a
              href={`tel:${PHONE_NUMBER}`}
              onClick={() => trackCTAClick("phone", "inquiry_sheet_mobile_header", PHONE_DISPLAY)}
              className="inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-primary/30 bg-background hover:bg-primary/5 text-primary text-xs font-semibold"
            >
              <Phone className="h-3.5 w-3.5" strokeWidth={1.75} />
              {t("inquiryForm.call")}
            </a>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-4 text-[11px] font-medium">
            <a
              href={`https://wa.me/${PHONE_WHATSAPP}?text=${encodeURIComponent(`Hallo, ich interessiere mich für den ${vehicleLabel}.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCTAClick("whatsapp", "inquiry_sheet_mobile_header", "WhatsApp")}
              className="inline-flex items-center gap-1 text-[hsl(145,65%,33%)]"
            >
              <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
              {t("inquiryForm.whatsapp")}
            </a>
            <span className="text-muted-foreground">·</span>
            <a
              href={`tel:${PHONE_NUMBER}`}
              onClick={() => trackCTAClick("phone", "inquiry_sheet_mobile_header", PHONE_DISPLAY)}
              className="inline-flex items-center gap-1 text-primary"
            >
              <Phone className="h-3.5 w-3.5" strokeWidth={1.75} />
              {t("inquiryForm.call")}
            </a>
          </div>
        )}
        {!mobileDetailsOpen && (
          <p className="text-[10px] text-center text-muted-foreground leading-tight">
            Antwort in &lt; 1 Std. · Es gilt unsere{" "}
            <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">
              Datenschutzerklärung
            </a>.
          </p>
        )}
      </div>



      {isLoading && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center rounded-md bg-background/70 backdrop-blur-sm"
          role="status"
          aria-live="polite"
        >
          <div className="flex flex-col items-center gap-2 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="text-sm font-medium text-foreground">Anfrage wird gesendet…</span>
          </div>
        </div>
      )}
    </form>
  );

  const desktopFormBody = (
    <div className="p-4 md:p-5 min-w-0 lg:flex lg:flex-col lg:h-full">


      <div className="flex items-start justify-between gap-2.5 mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="inline-flex items-center gap-2 gradient-primary text-primary-foreground px-3 py-1.5 rounded-md shadow-sm min-w-0">
            <Truck className="h-4 w-4 shrink-0" strokeWidth={1.5} />
            <h3 className="text-sm md:text-base font-bold leading-snug break-words">{vehicleLabel}</h3>
          </div>
        </div>
        {isInline && onClose && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={onClose}
            aria-label={t("inquiryForm.closeForm")}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="mb-3 space-y-0.5">
        <p className="text-[11px] md:text-xs text-muted-foreground leading-tight">
          {t("inquiryForm.stepLabel", { step, total: 2, stepTitle: step === 1 ? t("inquiryForm.step1Title") : t("inquiryForm.step2Title") })}
        </p>
        {headerFromPrice != null && (
          <p className="text-[11px] md:text-xs font-semibold text-primary leading-tight">
            {t("inquiryForm.fromPrice", { price: formatPrice(headerFromPrice) })}
          </p>
        )}
      </div>

      {/* Direkt-Kontakt-Bar: Alternative zum Formular */}
      <div className="mb-4 rounded-lg border border-border bg-secondary/30">
        <button
          type="button"
          onClick={() => setDirectOpen((v) => !v)}
          aria-expanded={directOpen}
          aria-controls="direct-contact-row"
          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left"
        >
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t("inquiryForm.direct")}
          </span>
          <ChevronDown
            className={cn("h-4 w-4 text-muted-foreground transition-transform", directOpen && "rotate-180")}
            strokeWidth={1.5}
          />
        </button>
        {directOpen && (
          <div id="direct-contact-row" className="grid grid-cols-2 gap-2 px-2.5 pb-2.5">
            <a
              href={`https://wa.me/${PHONE_WHATSAPP}?text=${encodeURIComponent(`Hallo, ich interessiere mich für den ${vehicleLabel}.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCTAClick("whatsapp", "inquiry_sheet_header", "WhatsApp")}
              className="inline-flex items-center justify-center gap-1.5 h-10 rounded-md bg-[hsl(145,65%,39%)] hover:bg-[hsl(145,65%,33%)] text-white text-sm font-semibold transition-colors"
              aria-label="WhatsApp Chat starten"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
              WhatsApp
            </a>
            <a
              href={`tel:${PHONE_NUMBER}`}
              onClick={() => trackCTAClick("phone", "inquiry_sheet_header", PHONE_DISPLAY)}
              className="inline-flex items-center justify-center gap-1.5 h-10 rounded-md border border-primary/30 bg-background hover:bg-primary/5 text-primary text-sm font-semibold transition-colors"
              aria-label={`Anrufen ${PHONE_DISPLAY}`}
            >
              <Phone className="h-4 w-4" strokeWidth={1.75} />
              Anrufen
            </a>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className={cn("h-1 flex-1 rounded-full transition-colors", step >= 1 ? "bg-primary" : "bg-border")} />
        <div className={cn("h-1 flex-1 rounded-full transition-colors", step >= 2 ? "bg-primary" : "bg-border")} />
      </div>

      {technicalSpecs.length > 0 && (
        <Collapsible open={techDataOpen} onOpenChange={setTechDataOpen} className="mb-4">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-border bg-secondary/30 hover:bg-secondary/50 transition-colors text-left"
              aria-label="Technische Daten anzeigen"
            >
              <span className="flex items-center gap-2 text-xs font-medium text-foreground">
                <Settings className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                Technische Daten {techDataOpen ? "ausblenden" : "anzeigen"}
              </span>
              <ChevronDown
                className={cn("h-4 w-4 text-muted-foreground transition-transform", techDataOpen && "rotate-180")}
                strokeWidth={1.5}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <div className="mt-2 rounded-md border border-border overflow-hidden">
              <table className="w-full">
                <tbody>
                  {technicalSpecs.map((spec, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-secondary/20" : ""}>
                      <td className="py-2 px-3 text-xs font-medium border-r border-border">{spec.label}</td>
                      <td className="py-2 px-3 text-xs text-muted-foreground">{spec.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {step === 1 && (
        <div className="space-y-3 lg:flex lg:flex-col lg:flex-1 lg:min-h-0">

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium">
                Abholung
                {submitErrors.pickupDate && <span className="text-destructive font-semibold"> – bitte wählen</span>}
              </Label>
              <Popover open={pickupPopoverOpen} onOpenChange={setPickupPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    ref={registerField("pickupDate") as React.Ref<HTMLButtonElement>}
                    variant="outline"
                    aria-invalid={!!submitErrors.pickupDate}
                    className={cn("w-full mt-1.5 justify-start text-left text-sm font-normal h-10", !formData.pickupDate && "text-muted-foreground", errorCn("pickupDate"))}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.pickupDate ? format(formData.pickupDate, "dd.MM.yyyy", { locale: de }) : "Datum wählen"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[60]" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.pickupDate || undefined}
                    onSelect={(d) => { clearSubmitError("pickupDate"); setDateUnknown(false); setFormData((p) => ({ ...p, pickupDate: d || null, returnDate: null })); setPickupPopoverOpen(false); }}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                    locale={de}
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              {errorHint("pickupDate")}
            </div>
            <div>
              <Label className="text-xs font-medium">Rückgabe <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Popover open={returnPopoverOpen} onOpenChange={setReturnPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full mt-1.5 justify-start text-left text-sm font-normal h-10", !formData.returnDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.returnDate ? format(formData.returnDate, "dd.MM.yyyy", { locale: de }) : "Datum wählen"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[60]" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.returnDate || undefined}
                    onSelect={(d) => { setFormData((p) => ({ ...p, returnDate: d || null })); setReturnPopoverOpen(false); }}
                    disabled={(d) => formData.pickupDate ? d <= formData.pickupDate : d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                    locale={de}
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="desktop-date-unknown"
              checked={dateUnknown}
              onCheckedChange={(checked) => {
                const next = checked === true;
                setDateUnknown(next);
                clearSubmitError("pickupDate");
                if (next) setFormData((p) => ({ ...p, pickupDate: null, returnDate: null }));
              }}
            />
            <label htmlFor="desktop-date-unknown" className="min-h-11 flex items-center text-sm font-medium cursor-pointer">
              {t("inquiryForm.dateUnknown")}
            </label>
          </div>


          <div>
            <Label className="text-xs font-medium">
              Zusätzliche Kilometer
              {priceCalc && priceCalc.includedKm > 0 && (
                <span className="text-muted-foreground font-normal">
                  {" "}(über die inklusiven {priceCalc.includedKm} km hinaus)
                </span>
              )}
            </Label>
            {priceCalc && priceCalc.includedKm > 0 && (
              <div className="lg:hidden mt-1.5 flex items-center gap-2 rounded-md border border-success/25 bg-success/5 px-2.5 py-1.5 text-xs">
                <Check className="h-3.5 w-3.5 text-success shrink-0" strokeWidth={2.5} />
                <span className="text-foreground">
                  <strong className="text-success">{priceCalc.includedKm} km bereits im Paket enthalten</strong>
                  <span className="text-muted-foreground"> – hier nur zusätzlich benötigte km eintragen.</span>
                </span>
              </div>
            )}

            <div className="relative mt-1.5">
              <Input
                type="number"
                min={0}
                step={50}
                value={formData.extraKm}
                onChange={(e) => setFormData((p) => ({ ...p, extraKm: e.target.value }))}
                placeholder="z.B. 200"
                className="h-10 text-sm pr-16"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">km</span>
            </div>
            {extraKmNum > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {extraKmNum} km × {formatPrice(EXTRA_KM_RATE)} € = {formatPrice(extraKmCost)} € netto
              </p>
            )}
          </div>


          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="delivery-toggle"
                checked={formData.deliveryEnabled}
                onCheckedChange={(c) => setFormData((p) => ({ ...p, deliveryEnabled: c === true }))}
              />
              <label htmlFor="delivery-toggle" className="text-sm font-medium cursor-pointer flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                Anlieferung gewünscht
              </label>
            </div>
            {formData.deliveryEnabled && (
              <div className="animate-fade-in-up">
                <Input
                  value={formData.deliveryAddress}
                  onChange={(e) => setFormData((p) => ({ ...p, deliveryAddress: e.target.value }))}
                  placeholder="Lieferadresse eingeben"
                  className="h-10 text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Info className="h-3 w-3 shrink-0" />
                  Anlieferungskosten: 2 € netto pro Kilometer (einfache Strecke ab Frankfurt-Rödelheim).
                </p>
              </div>
            )}
          </div>

          {isKuehl && (() => {
            const freshOnly = isFreshOnlyCoolingVehicle(vehicleLabel);
            return (
              <div>
                <Label className="text-xs font-medium">Temperaturanforderung</Label>
                <Select value={formData.temperature} onValueChange={(v) => setFormData((p) => ({ ...p, temperature: v }))}>
                  <SelectTrigger className="mt-1.5 h-10 text-sm" aria-label="Temperatur wählen">
                    <SelectValue placeholder="Temperatur wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0 bis +5 °C">0 bis +5 °C (Frischware)</SelectItem>
                    {!freshOnly && (
                      <SelectItem value="-18 bis -25 °C">-18 bis -25 °C (Tiefkühl)</SelectItem>
                    )}
                    <SelectItem value="+2 bis +8 °C">+2 bis +8 °C (Pharma)</SelectItem>
                    <SelectItem value="Individuell">Individuell</SelectItem>
                  </SelectContent>
                </Select>
                {freshOnly && (
                  <p className="text-xs text-muted-foreground mt-1.5 flex items-start gap-1">
                    <Info className="h-3 w-3 shrink-0 mt-0.5" />
                    Der Vito kühlt 0 bis +7 °C. Für Tiefkühl wählen Sie bitte den Sprinter Kühltransporter.
                  </p>
                )}
              </div>
            );
          })()}

          {priceCalc && (
            <div className="lg:hidden relative overflow-hidden min-w-0 bg-gradient-to-br from-primary/10 via-primary/5 to-accent/5 border-2 border-t-sky-400 border-primary/30 rounded-lg shadow-sm animate-fade-in-up">

              <div className="p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                {/* Left: prices side by side */}
                <div className="flex items-baseline gap-4 shrink-0">
                  <div>
                    <div className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">Netto</div>
                    <div className="text-2xl md:text-3xl font-bold text-primary leading-none tabular-nums">
                      {formatPrice(priceCalc.nettoTotal)}<span className="text-base ml-0.5">€</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">Brutto</div>
                    <div className="text-2xl md:text-3xl font-semibold text-foreground leading-none tabular-nums">
                      {formatPrice(priceCalc.total)}<span className="text-base ml-0.5">€</span>
                    </div>
                  </div>
                </div>

                {/* Right: dates + meta */}
                <div className="flex-1 min-w-0 w-full sm:w-auto border-t sm:border-t-0 sm:border-l border-primary/15 pt-2 sm:pt-0 sm:pl-3 space-y-1">
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs font-semibold text-foreground">
                    <span className="tabular-nums">{formData.pickupDate ? format(formData.pickupDate, "dd.MM.", { locale: de }) : "–"}</span>
                    <ArrowRight className="h-3 w-3 text-primary shrink-0" />
                    <span className="tabular-nums">{formData.returnDate ? format(formData.returnDate, "dd.MM.yyyy", { locale: de }) : "–"}</span>
                    <span className="text-muted-foreground font-normal">· {priceCalc.days} Tag{priceCalc.days !== 1 ? "e" : ""}</span>
                    {priceCalc.days > 0 && (
                      <span className="text-muted-foreground font-normal tabular-nums">· ø {formatPrice(priceCalc.nettoTotal / priceCalc.days)} € netto / Tag</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 items-center">
                    {priceCalc.includedKm > 0 && (
                      <span className="inline-flex items-center gap-1 bg-success/10 text-success border border-success/20 rounded px-1.5 py-0.5 text-[10px] font-semibold">
                        <Check className="h-2.5 w-2.5" />
                        {priceCalc.includedKm} km inklusive
                      </span>
                    )}
                    {extraKmNum > 0 && (
                      <span className="inline-flex items-center bg-background border border-border rounded px-1.5 py-0.5 text-[10px]">
                        +{extraKmNum} km · {formatPrice(extraKmCost)} €
                      </span>
                    )}
                    {formData.deliveryEnabled && (
                      <span className="inline-flex items-center bg-background border border-border rounded px-1.5 py-0.5 text-[10px] text-muted-foreground italic">
                        + Anlieferung
                      </span>
                    )}
                    {priceCalc.weekendApplied && (
                      <span className="inline-flex items-center gap-0.5 bg-primary/10 text-primary border border-primary/20 rounded px-1.5 py-0.5 text-[10px] font-semibold">
                        <Check className="h-2.5 w-2.5" />
                        Weekend
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground">inkl. 19 % MwSt. ({formatPrice(priceCalc.tax)} €)</div>
                </div>
              </div>
            </div>
          )}


          <div className="sticky bottom-0 -mx-4 md:-mx-5 px-4 md:px-5 py-3 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85 border-t border-border z-10 space-y-2 lg:static lg:mt-auto lg:mx-0 lg:px-0 lg:pt-4 lg:pb-0 lg:bg-transparent lg:backdrop-blur-0 lg:border-t-0 lg:supports-[backdrop-filter]:bg-transparent">
            {upsellHint && (
              <div className="lg:hidden bg-accent/5 border border-accent/15 rounded-md p-2 animate-fade-in-up">
                <div className="flex items-center gap-2 flex-wrap">
                  <Sparkles className="h-4 w-4 text-accent shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-semibold text-foreground mr-1">{t("inquiryForm.savingsTip")}</span>
                    <span className="text-xs text-muted-foreground leading-snug">{upsellHint.message}</span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs shrink-0"
                    onClick={() => applyUpsell(upsellHint.suggestedDays)}
                  >
                    {t("inquiryForm.savingsTipButton", { days: upsellHint.suggestedDays })}
                  </Button>
                </div>
              </div>
            )}

            <Button
              type="button"
              className="w-full h-12 gradient-primary hover:opacity-90"
              disabled={!canProceedToStep2}
              onClick={() => goToStep2(!!formData.pickupDate)}
            >
              <span className="flex items-center justify-center w-full gap-2">
                <span className="font-semibold">{t("inquiryForm.continueToContact")}</span>
                <ArrowRight className="h-4 w-4" />
              </span>
            </Button>
            <button
              type="button"
               onClick={() => { setDateUnknown(true); goToStep2(false); }}
              className="w-full text-center text-xs text-muted-foreground hover:text-primary underline underline-offset-2 transition-colors py-1"
            >
              {canProceedToStep2
                ? t("inquiryForm.skipDateFixed")
                : t("inquiryForm.skipDateUnclear")}
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <form onSubmit={handleSubmit} className="space-y-3 relative lg:flex lg:flex-col lg:flex-1 lg:min-h-0">

          <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2.5">
            <p className="text-sm font-bold text-primary flex items-center gap-1.5">
              <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} />
              {t("inquiryForm.trustTitle")}
            </p>
            <p className="text-xs text-muted-foreground mt-1 leading-snug">
              {t("inquiryForm.trustText")}
            </p>
          </div>

          <RadioGroup value={contactPreference} onValueChange={(value) => {
            const preference = value as "phone" | "email";
            setContactPreference(preference);
            clearSubmitError(preference === "phone" ? "email" : "phone");
          }} className="grid grid-cols-2 gap-2" aria-label={t("inquiryForm.contactPreference") }>
            <label htmlFor="desktop-contact-phone" className={cn("flex min-h-10 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm font-semibold", contactPreference === "phone" ? "border-primary bg-primary/5 text-primary" : "border-border")}>
              <RadioGroupItem id="desktop-contact-phone" value="phone" />
              {t("inquiryForm.phoneLabel")}
            </label>
            <label htmlFor="desktop-contact-email" className={cn("flex min-h-10 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm font-semibold", contactPreference === "email" ? "border-primary bg-primary/5 text-primary" : "border-border")}>
              <RadioGroupItem id="desktop-contact-email" value="email" />
              {t("inquiryForm.emailLabel")}
            </label>
          </RadioGroup>

          {(priceCalc || headerFromPrice != null) && (
            <div className="lg:hidden rounded-md border border-primary/20 bg-primary/5 px-3 py-2 flex items-center gap-2 text-xs">

              <Calculator className="h-3.5 w-3.5 text-primary shrink-0" />
              {priceCalc ? (
                <span className="text-foreground">
                  ca. <strong className="tabular-nums">{formatPrice(priceCalc.nettoTotal)} €</strong> netto für {priceCalc.days} Tag{priceCalc.days !== 1 ? "e" : ""}
                  <span className="text-muted-foreground"> · brutto {formatPrice(priceCalc.total)} €</span>
                </span>
              ) : (
                <span className="text-foreground">
                  ab <strong className="tabular-nums">{formatPrice(headerFromPrice ?? 0)} €</strong> netto/Tag – Gesamtpreis nach Zeitraumauswahl
                </span>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs font-medium">
              Name <span className="text-destructive">*</span>
              {submitErrors.name && <span className="text-destructive font-semibold"> – bitte ausfüllen</span>}
            </Label>
            <div className="relative">
              <Input
                ref={registerField("name")}
                disabled={isLoading}
                autoComplete="name"
                aria-invalid={!!submitErrors.name}
                value={formData.name}
                onFocusCapture={() => trackFieldFocus("name")}
                onChange={(e) => { clearSubmitError("name"); setFormData((p) => ({ ...p, name: e.target.value })); }}
                onBlur={(e) => handleBlur("name", e.target.value)}
                placeholder="z.B. Max Mustermann oder Frau Schmidt"
                className={cn("mt-1 h-9 text-sm pr-8", inputCn("name"), errorCn("name"))}
              />
              {renderFieldIcon("name")}
            </div>
            {errorHint("name")}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            <div>
              <Label className="text-xs font-medium">
                Telefon {contactPreference === "phone" && <span className="text-destructive">*</span>}
                {submitErrors.phone && <span className="text-destructive font-semibold"> – bitte ausfüllen</span>}
              </Label>
              <div className="relative">
                <Input ref={registerField("phone")} disabled={isLoading} type="tel" inputMode="tel" autoComplete="tel" maxLength={40} aria-invalid={!!submitErrors.phone} aria-describedby={submitErrors.phone ? "phone-error" : undefined} value={formData.phone} onFocusCapture={() => trackFieldFocus("phone")} onChange={(e) => { clearSubmitError("phone"); clearSubmitError("email"); setFormData((p) => ({ ...p, phone: e.target.value })); }} onBlur={(e) => handleBlur("phone", e.target.value)} placeholder="+49 170 1234567" className={cn("mt-1 h-9 text-sm pr-8", inputCn("phone"), errorCn("phone"))} />
                {renderFieldIcon("phone")}
              </div>
              {errorHint("phone")}
            </div>
            <div>
              <Label className="text-xs font-medium">
                E-Mail {contactPreference === "email" && <span className="text-destructive">*</span>}
                {submitErrors.email && <span className="text-destructive font-semibold"> – bitte prüfen</span>}
              </Label>
              <div className="relative">
                <Input ref={registerField("email")} disabled={isLoading} type="email" inputMode="email" autoComplete="email" maxLength={255} aria-invalid={!!submitErrors.email} aria-describedby={submitErrors.email ? "email-error" : undefined} value={formData.email} onFocusCapture={() => trackFieldFocus("email")} onChange={(e) => { clearSubmitError("phone"); clearSubmitError("email"); setFormData((p) => ({ ...p, email: e.target.value })); }} onBlur={(e) => handleBlur("email", e.target.value)} placeholder="max@beispiel.de" className={cn("mt-1 h-9 text-sm pr-8", inputCn("email"), errorCn("email"))} />
                {renderFieldIcon("email")}
              </div>
              {errorHint("email")}
            </div>
          </div>


          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            <div>
              <Label className="text-xs font-medium">{t("inquiryForm.companyLabel")}</Label>
              <Input disabled={isLoading} value={formData.company} onChange={(e) => setFormData((p) => ({ ...p, company: e.target.value }))} placeholder={t("inquiryForm.companyPlaceholder")} className="mt-1 h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs font-medium">{t("inquiryForm.messageLabel")}</Label>
              <Input
                disabled={isLoading}
                value={formData.usage}
                onChange={(e) => setFormData((p) => ({ ...p, usage: e.target.value }))}
                placeholder={isKuehl ? t("inquiryForm.messagePlaceholderCooling") : t("inquiryForm.messagePlaceholderTransporter")}
                className="mt-1 h-9 text-sm"
              />
            </div>
          </div>

          <p className="text-[10px] text-muted-foreground leading-snug -mt-1">
            {t("inquiryForm.draftNotice")}
          </p>

          <div className="sticky bottom-0 -mx-4 md:-mx-5 px-4 md:px-5 py-3 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85 border-t border-border z-10 lg:static lg:mt-auto lg:mx-0 lg:px-0 lg:pt-4 lg:pb-0 lg:bg-transparent lg:backdrop-blur-0 lg:border-t-0 lg:supports-[backdrop-filter]:bg-transparent">
            <div className="flex gap-2.5">
              <Button type="button" variant="outline" className="h-12 flex-1" onClick={() => setStep(1)} disabled={isLoading}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("inquiryForm.back")}
              </Button>
              <Button type="submit" className="h-12 flex-[2] min-w-0 gradient-primary hover:opacity-90" disabled={isLoading} aria-busy={isLoading}>
                <span className="flex items-center justify-center w-full gap-2 min-w-0">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : <Send className="h-4 w-4 shrink-0" />}
                  <span className="font-semibold truncate">{isLoading ? t("inquiryForm.sending") : t("inquiryForm.submit")}</span>
                  {isKuehl && priceCalc && !isLoading && (
                    <span className="hidden min-[420px]:inline font-bold tabular-nums shrink-0">· {formatPrice(priceCalc.nettoTotal)} €</span>
                  )}
                </span>
              </Button>
            </div>
            <p className="mt-2 text-[10px] text-muted-foreground leading-snug text-center">
              Mit dem Absenden akzeptieren Sie unsere{" "}
              <a href="/datenschutz" className="underline hover:text-primary" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a>. Wir melden uns in unter 1 Stunde – unverbindlich &amp; kostenlos.
            </p>
          </div>

          {isLoading && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-background/70 backdrop-blur-sm animate-fade-in"
              role="status"
              aria-live="polite"
            >
              <div className="flex flex-col items-center gap-2 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm font-medium text-foreground">Anfrage wird gesendet…</span>
                <span className="text-xs text-muted-foreground">Bitte einen Moment Geduld</span>
              </div>
            </div>
          )}
        </form>
      )}
    </div>
  );

  const formBody = isMobileCompact ? mobileFormBody : desktopFormBody;

  const sideImage = (
    <div className="hidden lg:flex flex-col justify-center bg-secondary/20 border-l border-border">
      <div className="p-5 space-y-4 flex flex-col items-center">
        {vehicleImage && (
          <div className="rounded-md bg-background border border-border overflow-hidden flex items-center justify-center aspect-[16/11] w-full max-w-md mx-auto">
            <img
              src={vehicleImage}
              alt={vehicleLabel}
              className="w-full h-full object-contain"
            />
          </div>
        )}

        {priceCalc ? (
          <>
            {/* Preiskachel */}
            <div className="rounded-lg border-2 border-t-sky-400 border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-accent/5 p-4 shadow-sm animate-fade-in-up">
              <div className="flex items-baseline gap-4">
                <div>
                  <div className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">Netto</div>
                  <div className="text-3xl font-bold text-primary leading-none tabular-nums">
                    {formatPrice(priceCalc.nettoTotal)}<span className="text-sm ml-0.5">€</span>
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">Brutto</div>
                  <div className="text-3xl font-semibold text-foreground leading-none tabular-nums">
                    {formatPrice(priceCalc.total)}<span className="text-sm ml-0.5">€</span>
                  </div>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-primary/15 text-[11px] text-foreground/90 space-y-0.5">
                <div className="flex flex-wrap items-center gap-x-1.5">
                  <span className="tabular-nums font-semibold">{formData.pickupDate ? format(formData.pickupDate, "dd.MM.", { locale: de }) : "–"}</span>
                  <ArrowRight className="h-3 w-3 text-primary shrink-0" />
                  <span className="tabular-nums font-semibold">{formData.returnDate ? format(formData.returnDate, "dd.MM.yyyy", { locale: de }) : "–"}</span>
                  <span className="text-muted-foreground">· {priceCalc.days} Tag{priceCalc.days !== 1 ? "e" : ""}</span>
                </div>
                {priceCalc.days > 0 && (
                  <div className="text-[10px] text-muted-foreground tabular-nums">
                    ø {formatPrice(priceCalc.nettoTotal / priceCalc.days)} € netto / Tag
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground">inkl. 19 % MwSt. ({formatPrice(priceCalc.tax)} €)</div>
              </div>
            </div>

            {/* Kilometer-Kachel */}
            {priceCalc.includedKm > 0 && (
              <div className="rounded-lg border border-success/25 bg-success/5 p-3">
                <div className="flex items-start gap-2">
                  <div className="min-w-0">
                    <div className="text-base font-bold text-success leading-tight tabular-nums">
                      {priceCalc.includedKm} km inklusive
                    </div>
                    <div className="text-[11px] text-muted-foreground leading-snug">
                      Bereits im Paketpreis enthalten.
                    </div>
                  </div>
                </div>
                {extraKmNum > 0 && (
                  <div className="mt-2 pt-2 border-t border-success/20 text-[11px] text-foreground/90">
                    <div className="font-semibold">+{extraKmNum} km Extra</div>
                    <div className="text-muted-foreground tabular-nums">
                      {extraKmNum} × {formatPrice(EXTRA_KM_RATE)} € = {formatPrice(extraKmCost)} € netto
                    </div>
                  </div>
                )}
                <div className="mt-1.5 text-[10px] text-muted-foreground">
                  Extra-km: {formatPrice(EXTRA_KM_RATE)} € netto / km
                </div>
              </div>
            )}

            {/* Chips */}
            {(priceCalc.weekendApplied || formData.deliveryEnabled) && (
              <div className="flex flex-wrap gap-1">
                {priceCalc.weekendApplied && (
                  <span className="inline-flex items-center gap-0.5 bg-primary/10 text-primary border border-primary/20 rounded px-1.5 py-0.5 text-[10px] font-semibold">
                    Weekend-Paket
                  </span>
                )}
                {formData.deliveryEnabled && (
                  <span className="inline-flex items-center bg-background border border-border rounded px-1.5 py-0.5 text-[10px] text-muted-foreground italic">
                    + Anlieferung
                  </span>
                )}
              </div>
            )}

            {/* Spar-Tipp (statt sticky über CTA) */}
            {upsellHint && (
              <div className="rounded-lg border border-accent/25 bg-accent/5 p-3 animate-fade-in-up">
                <div className="flex items-start gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-accent shrink-0 mt-0.5" strokeWidth={1.75} />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-foreground">{t("inquiryForm.savingsTip")}</div>
                    <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                      {upsellHint.message}
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-full text-xs"
                  onClick={() => applyUpsell(upsellHint.suggestedDays)}
                >
                  {t("inquiryForm.savingsTipAdjust", { days: upsellHint.suggestedDays })}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-border bg-background/50 p-4 text-center">
            <Calculator className="h-5 w-5 text-muted-foreground mx-auto mb-2" strokeWidth={1.5} />
            {headerFromPrice != null && (
              <div className="mb-1.5 text-sm font-bold text-primary tabular-nums">
                {t("inquiryForm.fromPrice", { price: formatPrice(headerFromPrice) })}
              </div>
            )}
            <div className="text-xs font-medium text-foreground">{t("inquiryForm.pricePlaceholderTitle")}</div>
            <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
              {t("inquiryForm.pricePlaceholderText")}
            </div>
          </div>
        )}

      </div>
    </div>
  );


  const successDialog = (
    <InquirySuccessDialog
      open={successOpen}
      onOpenChange={(o) => {
        setSuccessOpen(o);
        if (!o) {
          onSubmitted?.();
          onClose?.();
        }
      }}
      contactMethod={successContactMethod}
    />
  );

  if (isInline) {
    return (
      <>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,400px]">
          <div>{formBody}</div>
          {sideImage}
        </div>
        {successDialog}
      </>
    );
  }

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sheetScrollMilestonesRef = useRef<Set<number>>(new Set());
  const onSheetScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const max = el.scrollHeight - el.clientHeight;
    if (max <= 0) return;
    const depth = Math.round((el.scrollTop / max) * 100);
    setInquirySheetScrollDepth(depth);
    for (const m of [25, 50, 75, 100] as const) {
      if (depth >= m && !sheetScrollMilestonesRef.current.has(m)) {
        sheetScrollMilestonesRef.current.add(m);
        trackInquirySheetScroll({ depth: m, vehicle_type: vehicleType, vehicle_id: vehicleId ?? null });
      }
    }
  };

  return (
    <>
      <div
        ref={scrollContainerRef}
        onScroll={onSheetScroll}
        className="grid grid-cols-1 lg:grid-cols-[1fr,400px] min-w-0 w-full max-w-full max-h-[95dvh] overflow-y-auto overflow-x-hidden overscroll-contain"
      >
        <div className="min-w-0 max-w-full">
          {formBody}
        </div>
        {sideImage}
      </div>
      {successDialog}
    </>
  );
};



export default InquiryForm;
